import { useQuery } from "@tanstack/react-query";
import type { NotificationHistory } from "../api/api";
// eslint-disable-next-line no-duplicate-imports
import { getNotificationsHistory } from "../api/api";
import { format } from "date-fns/format";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import logoLtcaMini from "../assets/logo-ltca-mini.png";
import { Switch } from "@/components/ui/switch";
// import { FirebaseMessaging } from "@capacitor-firebase/messaging";
import { LoaderSpinner } from "@/components/ui/LoaderSpinner";
// import { Toast } from "@capacitor/toast";
// import { useStorage } from "@/context/StorageContext";
import OneSignal, {
	type PushSubscriptionChangedState,
} from "onesignal-cordova-plugin";
import { Capacitor } from "@capacitor/core";

interface NotificationProps {
	title: string;
	date: string;
	description: string;
	open: boolean;
	route: string;
	body?: string;
}

const Notification: React.FC<NotificationProps> = (props): JSX.Element => {
	const { title, date, description, open, route } = props;
	const navigate = useNavigate();

	const redirect = async (): Promise<void> => {
		if (route) {
			await navigate({ to: route });
		}
	};

	return (
		<div
			className="flex items-start w-full mb-4 bg-white p-3 rounded-lg"
			onClick={redirect}
		>
			<div className="min-w-[43px] h-content mr-3 bg-logo p-2 rounded-full">
				<img alt="" className="w-[30px] h-[30px] m-auto" src={logoLtcaMini} />
			</div>
			<div className="flex-1">
				<div className="flex justify-between items-center mb-1">
					<h3 className="text-sm text-slate-400 font-medium max-w-[65%] line-clamp-2">
						{title}
					</h3>
					<div>
						<span className="text-sm text-slate-400 mr-2">{date}</span>
						{/* <span className="text-sm text-slate-400 mr-2">{format(date * 1000, "dd.MM.yyyy")}</span> */}
						{open ? (
							<span className="inline-block bg-red w-3 h-3 rounded-full" />
						) : (
							<span className="inline-block bg-slate-600 w-3 h-3 rounded-full" />
						)}
					</div>
				</div>
				<div className="inline text-sm font-light line-clamp-2">
					{description}
				</div>
			</div>
		</div>
	);
};

// const checkNotificationPermissions = async (): Promise<
// 	"granted" | "denied" | "prompt" | "prompt-with-rationale" | "unknown"
// > => {
// 	try {
// 		const result = await FirebaseMessaging.checkPermissions();
// 		return result.receive;
// 	} catch (error) {
// 		console.error("Error checking notification permissions:", error);
// 		return "unknown";
// 	}
// };

// Włącz subskrypcję do topicu all_users
// const enableTopicSubscription = async (
// 	setTopicSubscriptionValue: (value: boolean) => Promise<void>
// ): Promise<boolean> => {
// 	try {
// 		// Najpierw sprawdź uprawnienia
// 		const permissions = await checkNotificationPermissions();
// 		if (permissions !== "granted") {
// 			const result = await FirebaseMessaging.requestPermissions();
// 			if (result.receive !== "granted") {
// 				await Toast.show({ text: "Uprawnienia do notyfikacji są wymagane" });
// 				return false;
// 			}
// 		}

// 		// Subskrybuj do topicu
// 		await FirebaseMessaging.subscribeToTopic({ topic: "all_users" });

// 		// Zapisz stan lokalnie
// 		await setTopicSubscriptionValue(true);

// 		await Toast.show({ text: "Notyfikacje włączone" });
// 		return true;
// 	} catch (error) {
// 		console.error("Error enabling topic subscription:", error);
// 		await Toast.show({ text: "Błąd podczas włączania notyfikacji" });
// 		return false;
// 	}
// };

// Wyłącz subskrypcję z topicu all_users
// const disableTopicSubscription = async (
// 	setTopicSubscriptionValue: (value: boolean) => Promise<void>
// ): Promise<boolean> => {
// 	try {
// 		await FirebaseMessaging.unsubscribeFromTopic({ topic: "all_users" });

// 		// Usuń stan lokalny
// 		await setTopicSubscriptionValue(false);

// 		await Toast.show({ text: "Notyfikacje wyłączone" });
// 		return true;
// 	} catch (error) {
// 		console.error("Error disabling topic subscription:", error);
// 		await Toast.show({ text: "Błąd podczas wyłączania notyfikacji" });
// 		return false;
// 	}
// };

export const NotificationsPage = (): JSX.Element => {
	const { data: notificationsHistory, isLoading } =
		useQuery<NotificationHistory>({
			queryKey: ["notificationsHistory"],
			queryFn: getNotificationsHistory,
		});

	// const { topicSubscription, setTopicSubscriptionValue } = useStorage();

	const [isLoadingSubscription, setIsLoadingSubscription] =
		useState<boolean>(false);

	const [oneSignalSubscription, setOneSignalSubscription] = useState<
		boolean | "begin" | "error"
	>("begin");

	useEffect(() => {
		if (import.meta.env.VITE_ONE_SIGNAL_FEATURE_FLAG === "true" && !Capacitor.isNativePlatform()) {
			setIsLoadingSubscription(false);
			setOneSignalSubscription(false);
			return;
		}

		setIsLoadingSubscription(true);
		OneSignal.User.pushSubscription
			.getOptedInAsync()
			.then((optedIn) => {
				setIsLoadingSubscription(false);
				setOneSignalSubscription(optedIn);
			})
			.catch((error) => {
				setIsLoadingSubscription(false);
				setOneSignalSubscription("error");
				console.error("Error getting OneSignal subscription:", error);
			});
	}, []);

	useEffect(() => {
		if (import.meta.env.VITE_ONE_SIGNAL_FEATURE_FLAG === "true" && !Capacitor.isNativePlatform()) {
			return;
		}

		const subscriptionObserver = (
			event: PushSubscriptionChangedState
		): void => {
			setOneSignalSubscription(event.current.optedIn);
			setIsLoadingSubscription(false);
		};

		OneSignal.User.pushSubscription.addEventListener(
			"change",
			subscriptionObserver
		);

		return (): void => {
			OneSignal.User.pushSubscription.removeEventListener(
				"change",
				subscriptionObserver
			);
		};
	}, []);

	const handleOneSignalSubscriptionToggle = (): void => {
		if (!Capacitor.isNativePlatform()) {
			return;
		}

		setIsLoadingSubscription(true);
		if (oneSignalSubscription === true) {
			OneSignal.User.pushSubscription.optOut();
		} else {
			// Wywołanie optIn() może spowodować wyświetlenie natywnego promptu o zgodę
			OneSignal.User.pushSubscription.optIn();
		}
	};

	return (
		<main className="py-3 px-safe-offset-3 min-h-[calc(100vh-176px)] overflow-x-hidden max-w-[890px] mx-auto">
			<div className="flex justify-between items-center">
				<h2 className="text-base font-medium text-fontheader my-3">
					Powiadomienia
				</h2>
				{/* <div className="flex items-center gap-2">
					<span className="text-sm text-text3 pr-2">Aktywne</span>
					<Switch
						checked={topicSubscription ?? false}
						onCheckedChange={handleTopicSubscriptionToggle}
						disabled={isLoadingSubscription}
					/>
				</div> */}
				{import.meta.env.VITE_ONE_SIGNAL_FEATURE_FLAG === "true" &&
					Capacitor.isNativePlatform() && (
						<div>
							<Switch
								checked={
									oneSignalSubscription === "begin" ||
									oneSignalSubscription === "error"
										? false
										: oneSignalSubscription
								}
								onCheckedChange={handleOneSignalSubscriptionToggle}
								disabled={
									isLoadingSubscription || oneSignalSubscription === "begin"
								}
							/>
						</div>
					)}
			</div>
			{isLoading && <LoaderSpinner size={10} />}
			<div>
				{notificationsHistory?.notifications
					.slice()
					.reverse()
					.map((notification) => (
						<Notification
							key={notification.createdAt._seconds}
							date={format(
								notification.createdAt._seconds * 1000,
								"dd.MM.yyyy"
							)}
							description={notification.body}
							open={false}
							route={notification.route}
							title={notification.title}
						/>
					))}
			</div>
		</main>
	);
};
